import {useMemo, type JSX} from 'react';
import {createPortal} from 'react-dom';
import {Link} from 'react-router-dom';
import {useFloating, offset, flip, shift, autoUpdate} from '@floating-ui/react';
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/react';
import {EllipsisVerticalIcon} from '@heroicons/react/24/outline';
import {classNames} from 'utils';

type ItemType = {
  icon: any;
  text: string;
  path?: string;
  onClick?: () => void;
  color?: string;
};

interface IDropdownProps {
  items: ItemType[][];
  button?: JSX.Element;
  containerClass?: string;
}

const Dropdown = ({items, button, containerClass = 'relative inline-block text-left'}: IDropdownProps) => {
  const {x, y, refs, strategy} = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const dropdownButton = useMemo(() => {
    return (
      button ?? (
        <div className="inline-flex size-8 items-center justify-center rounded-full bg-transparent text-primary hover:brightness-120 cursor-pointer">
          <span className="sr-only">Open options</span>
          <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
        </div>
      )
    );
  }, [button]);

  return (
    <Menu as="div" className={containerClass}>
      <MenuButton ref={refs.setReference}>{dropdownButton}</MenuButton>
      {createPortal(
        <MenuItems
          transition
          ref={refs.setFloating}
          className="absolute right-0 top-10 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-500 shadow-lg transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: 50,
          }}>
          {items.map((subItems, i) => {
            return (
              <div key={i} className="py-1">
                {subItems.map((subItem, j) => {
                  const item = (() => {
                    if (subItem.path) return {component: Link, props: {to: subItem.path}};
                    if (subItem.onClick) return {component: 'button', props: {type: 'button', onClick: subItem.onClick}};
                    return null;
                  })();
                  if (!item) {
                    return null;
                  }
                  return (
                    <MenuItem key={`${i}-${j}`} as="div">
                      {/* @ts-ignore  */}
                      <item.component
                        {...item.props}
                        className={classNames(
                          'w-full group flex items-center px-4 py-2 text-sm data-focus:bg-gray-100 data-focus:text-gray-900 data-focus:outline-hidden cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700',
                          subItem.color ?? 'dark:text-white',
                        )}>
                        <subItem.icon
                          aria-hidden="true"
                          className={classNames('mr-3 size-5 group-data-focus:text-gray-500', subItem.color ?? 'text-gray-400')}
                        />
                        {subItem.text}
                      </item.component>
                    </MenuItem>
                  );
                })}
              </div>
            );
          })}
        </MenuItems>,
        document.body,
      )}
    </Menu>
  );
};

export default Dropdown;
